<template>
  <v-form ref="form" v-model="form_valid" class="billing-record-form">
    <v-alert type="info" text dense class="billing-form-alert mb-2">
      {{ $t("payment.billing_edit_recalculate_note") }}
    </v-alert>

    <v-dialog v-model="date_dialog" max-width="360" persistent>
      <v-card>
        <v-card-title class="payment-date-dialog-title">
          <v-icon color="#ff5722" class="mr-2">mdi-calendar-month-outline</v-icon>
          {{ $t("payment.select_payment_date") }}
        </v-card-title>
        <v-card-text class="pa-0">
          <v-date-picker
            v-model="date_picker_value"
            locale="th-TH"
            color="#ff5722"
            first-day-of-week="1"
            full-width
            scrollable
          />
        </v-card-text>
        <v-divider />
        <v-card-actions class="justify-end px-4 py-3 payment-date-dialog-actions">
          <v-btn outlined color="#ff5722" @click="date_dialog = false">
            {{ $t("payment.cancel") }}
          </v-btn>
          <v-btn
            color="#ff5722"
            dark
            depressed
            :disabled="!date_picker_value"
            @click="apply_pay_date"
          >
            {{ $t("payment.use_selected_date") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-simple-table dense class="billing-fee-table mt-2">
      <tbody>
        <tr v-for="field in visible_fee_fields" :key="field">
          <th>{{ fee_label(field) }}</th>
          <td>
            <v-text-field
              :value="value[field]"
              type="number"
              :min="field === 'special_fee2' ? null : 0"
              step="1"
              inputmode="numeric"
              dense
              outlined
              hide-details="auto"
              :suffix="$t('payment.baht')"
              :rules="amount_rules(field)"
              @input="update_field(field, $event)"
            />
          </td>
        </tr>
        <tr class="billing-total-row">
          <th>{{ $t("payment.new_total") }}</th>
          <td class="text-right">
            {{ format_amount(total) }} {{ $t("payment.baht") }}
          </td>
        </tr>
      </tbody>
    </v-simple-table>

    <v-row dense class="billing-meta-row mt-2">
      <v-col cols="12" md="6">
        <v-text-field
          :value="pay_date_input"
          :label="$t('payment.payment_date_buddhist')"
          :placeholder="$t('payment.payment_date_buddhist_placeholder')"
          outlined
          dense
          readonly
          :disabled="loading"
          hide-details="auto"
          prepend-inner-icon="mdi-calendar-month-outline"
          :rules="[required_rule]"
          class="payment-date-field"
          @click="open_date_picker"
          @click:prepend-inner="open_date_picker"
        />
      </v-col>
      <v-col cols="12" md="6">
        <v-autocomplete
          :value="value.bank"
          :items="bank_options"
          item-text="bank_name"
          item-value="bank_code"
          :label="$t('payment.bank')"
          :no-data-text="$t('payment.no_billing_codes')"
          outlined
          dense
          hide-details="auto"
          :rules="[bank_required_rule]"
          @input="update_field('bank', $event)"
        />
      </v-col>
    </v-row>

    <div class="billing-form-actions d-flex justify-end mt-3">
      <v-btn
        outlined
        color="#ff5722"
        :disabled="loading"
        @click="$emit('cancel')"
      >
        {{ $t("payment.cancel") }}
      </v-btn>
      <v-btn
        color="#ff5722"
        :dark="form_valid && !submit_disabled"
        depressed
        :disabled="!form_valid || submit_disabled"
        :loading="loading"
        @click="$emit('submit')"
      >
        {{ submit_label }}
      </v-btn>
    </div>
  </v-form>
</template>

<script>
export default {
  name: "PaymentBillingRecordForm",
  props: {
    value: {
      type: Object,
      required: true,
    },
    fee_fields: {
      type: Array,
      default: () => [],
    },
    visible_fee_fields: {
      type: Array,
      default: () => [],
    },
    bank_options: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
    submit_disabled: {
      type: Boolean,
      default: false,
    },
    submit_label: {
      type: String,
      required: true,
    },
  },
  data: () => ({
    form_valid: false,
    date_dialog: false,
    date_picker_value: "",
  }),
  computed: {
    pay_date_input() {
      return this.format_buddhist_date(this.value.pay_date);
    },
    total() {
      return this.fee_fields.reduce(
        (sum, field) => sum + (Number(this.value[field]) || 0),
        0
      );
    },
  },
  mounted() {
    this.$nextTick(() => this.$refs.form && this.$refs.form.validate());
  },
  methods: {
    format_buddhist_date(value) {
      const match = String(value || "").match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (!match) return "";
      const date = new Date(
        Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]))
      );
      return new Intl.DateTimeFormat("th-TH-u-ca-buddhist", {
        day: "numeric",
        month: "long",
        year: "numeric",
        timeZone: "UTC",
      }).format(date);
    },
    open_date_picker() {
      if (this.loading) return;
      const currentValue = String(this.value.pay_date || "").slice(0, 10);
      this.date_picker_value = /^\d{4}-\d{2}-\d{2}$/.test(currentValue)
        ? currentValue
        : new Date().toISOString().slice(0, 10);
      this.date_dialog = true;
    },
    apply_pay_date() {
      if (!this.date_picker_value) return;
      this.update_field("pay_date", this.date_picker_value);
      this.date_dialog = false;
      this.$nextTick(() => this.$refs.form && this.$refs.form.validate());
    },
    amount_rules(field) {
      const isInstituteDiscount = field === "special_fee2";
      return [
        this.required_rule,
        (value) =>
          (isInstituteDiscount
            ? /^-?\d+$/.test(String(value))
            : /^\d+$/.test(String(value))) ||
          this.$t(
            isInstituteDiscount
              ? "payment.amount_integer"
              : "payment.amount_nonnegative_integer"
          ),
      ];
    },
    required_rule(value) {
      return (
        (value !== null && value !== undefined && String(value).trim() !== "") ||
        this.$t("payment.amount_required")
      );
    },
    bank_required_rule(value) {
      // BANK_CODE ว่างเป็นตัวเลือก "ไม่มี" ได้ แต่ต้องเป็นรายการที่ API ส่งมา
      return (
        (typeof value === "string" &&
          this.bank_options.some((option) => option.bank_code === value)) ||
        this.$t("payment.bank_required")
      );
    },
    update_field(field, fieldValue) {
      this.$emit("input", { ...this.value, [field]: fieldValue });
    },
    fee_label(field) {
      const key = `payment.billing_field_${field}`;
      const translated = this.$t(key);
      return translated === key ? field : translated;
    },
    format_amount(value) {
      return Number(value || 0).toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
  },
};
</script>

<style scoped>
.billing-record-form {
  font-size: 1rem;
}
.billing-form-alert {
  font-size: 0.98rem;
}
.billing-meta-row {
  margin-bottom: 2px;
}
.payment-date-dialog-title {
  font-size: 1.1rem;
  font-weight: 700;
}
.payment-date-dialog-actions {
  gap: 10px;
}
.billing-fee-table {
  border-top: 1px solid rgba(0, 0, 0, 0.12);
}
.billing-fee-table tbody tr:nth-child(odd):not(.billing-total-row) {
  background: #fff;
}
.billing-fee-table tbody tr:nth-child(even):not(.billing-total-row) {
  background: #fffaf4;
}
.billing-fee-table th,
.billing-fee-table td {
  height: 39px !important;
  padding: 2px 12px !important;
  font-size: 0.98rem !important;
}
.billing-fee-table th {
  width: 58%;
  color: rgba(0, 0, 0, 0.78);
  font-weight: 600;
}
.billing-fee-table td {
  width: 42%;
}
.billing-total-row {
  background: #f5f5f5;
}
.billing-total-row th,
.billing-total-row td {
  height: 40px !important;
  color: rgba(0, 0, 0, 0.87);
  font-weight: 700;
}
.billing-form-actions {
  gap: 12px;
}
::v-deep .billing-fee-table .v-input {
  max-width: 300px;
  margin: 0 0 0 auto;
  font-size: 1rem;
}
::v-deep .billing-fee-table .v-input__slot {
  min-height: 33px !important;
  margin-bottom: 0 !important;
}
::v-deep .billing-fee-table .v-text-field__slot input {
  padding: 4px 0 !important;
  font-size: 1rem;
  text-align: right;
}
::v-deep .billing-fee-table .v-text-field__suffix {
  font-size: 0.95rem;
}
::v-deep .billing-meta-row .v-input__slot {
  min-height: 38px !important;
  margin-bottom: 0 !important;
}
::v-deep .payment-date-field .v-input__slot,
::v-deep .payment-date-field input {
  cursor: pointer !important;
}
@media (max-width: 600px) {
  .billing-fee-table th,
  .billing-fee-table td {
    padding: 3px 6px !important;
    font-size: 0.9rem !important;
  }
}
</style>
